import { HTMLAttributes, useRef } from "react";
import { AutocompleteRenderOptionState } from "@mui/material/Autocomplete";
import { TextField, TextFieldProps } from "@mui/material";
import StyledAutocomplete from "./StyledAutocomplete";
import { fullNameFromParts } from "helpers/name-helpers";
import { NamedUser } from "generated/graphql";

type AutocompleteUser = Omit<NamedUser, "__typename">;

const renderUser = (
  props: HTMLAttributes<HTMLLIElement>,
  user: AutocompleteUser,
  _state: AutocompleteRenderOptionState
) => {
  return (
    <li {...props} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }} key={user.id}>
      <p className="text-xs font-bold flex-nowrap">{fullNameFromParts(user.firstName, user.lastName)}</p>
      <p className="text-xs text-gray-400 flex-nowrap">{user.email}</p>
    </li>
  );
};

function sortNamedUsers(a: AutocompleteUser, b: AutocompleteUser): number {
  const aName = fullNameFromParts(a.firstName, b.lastName).toLowerCase();
  const bName = fullNameFromParts(b.firstName, b.lastName).toLowerCase();

  if (a.id === -1) return -1;
  if (b.id === -1) return 1;

  return aName.localeCompare(bName);
}

export interface UserAutocompleteProps {
  label: string;
  options: AutocompleteUser[];
  selected: AutocompleteUser | undefined;
  onChange: (field: AutocompleteUser | undefined) => void;
  variant?: TextFieldProps["variant"];
  disabled?: boolean;
  dataPendo?: string;
  textFieldProps?: TextFieldProps;
}

export default function UserAutoComplete(props: UserAutocompleteProps) {
  const autoCompleteRef = useRef<HTMLInputElement>(null);
  const options = props.options.slice().sort(sortNamedUsers);

  return (
    <StyledAutocomplete<AutocompleteUser, false, false, false>
      freeSolo={false}
      multiple={false}
      disabled={props.disabled}
      autoHighlight
      autoComplete
      size="small"
      style={{ fontSize: "0.8rem" }}
      fullWidth
      options={options}
      value={props.selected || null}
      onChange={(_evt, item) => {
        props.onChange(item || undefined);
      }}
      renderOption={renderUser}
      slotProps={{
        popper: {
          // min width of 350 but expand to atleast the width of the input
          style: {
            minWidth: autoCompleteRef.current?.offsetWidth || 350,
          },
        },
      }}
      getOptionLabel={(u: AutocompleteUser) => fullNameFromParts(u.firstName, u.lastName)}
      renderInput={(params) => (
        <TextField
          {...params}
          onClick={(evt) => evt.stopPropagation()}
          inputRef={autoCompleteRef}
          color="secondary"
          label={props.label}
          autoFocus
          variant={props.variant || "outlined"}
          fullWidth
          {...props.textFieldProps}
        />
      )}
      data-pendo={props.dataPendo}
    />
  );
}

export interface UserAutocompleteMultipleProps {
  label: string;
  options: AutocompleteUser[];
  selected: AutocompleteUser[];
  onChange: (field: AutocompleteUser[]) => void;
  variant?: TextFieldProps["variant"];
  disabled?: boolean;
  dataPendo?: string;
}

export function UserAutoCompleteMultiple(props: UserAutocompleteMultipleProps) {
  const autoCompleteRef = useRef<HTMLInputElement>(null);
  const options = props.options.slice().sort(sortNamedUsers);

  return (
    <StyledAutocomplete<AutocompleteUser, true, false, false>
      freeSolo={false}
      multiple
      disabled={props.disabled}
      autoHighlight
      autoComplete
      size="small"
      style={{ fontSize: "0.8rem" }}
      fullWidth
      options={options}
      value={props.selected}
      onChange={(_evt, items) => {
        props.onChange(items);
      }}
      renderOption={renderUser}
      slotProps={{
        popper: {
          // min width of 350 but expand to atleast the width of the input
          style: {
            minWidth: autoCompleteRef.current?.offsetWidth || 350,
          },
        },
      }}
      getOptionLabel={(u: AutocompleteUser) => fullNameFromParts(u.firstName, u.lastName)}
      renderInput={(params) => (
        <TextField
          {...params}
          onClick={(evt) => evt.stopPropagation()}
          inputRef={autoCompleteRef}
          color="secondary"
          label={props.label}
          variant={props.variant || "outlined"}
          fullWidth
        />
      )}
      data-pendo={props.dataPendo}
    />
  );
}
